/** Browser events for hub live SSE (presence tab stream) health. */

export const HUB_LIVE_CONNECTION_EVENT = "hub:live-connection";

export type HubLiveConnectionStatus =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected";

export type HubLiveConnectionReason = "ok" | "auth" | "terminal" | "transport" | "stopped";

export type HubLiveConnectionDetail = {
  status: HubLiveConnectionStatus;
  reason: HubLiveConnectionReason;
};

export const HUB_LIVE_CONNECTION_DISCONNECTED_MESSAGE =
  "Hub live connection is down. Presence and live updates are paused until the connection recovers.";

export const HUB_LIVE_CONNECTION_AUTH_MESSAGE =
  "Hub live connection stopped (session expired). Sign in again to restore presence and live updates.";

export const HUB_LIVE_CONNECTION_RECONNECTING_MESSAGE =
  "Reconnecting to the hub for live updates…";

let currentDetail: HubLiveConnectionDetail = {
  status: "connecting",
  reason: "ok",
};

export function getHubLiveConnectionDetail(): HubLiveConnectionDetail {
  return currentDetail;
}

export function signalHubLiveConnection(detail: HubLiveConnectionDetail): void {
  currentDetail = detail;
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
    return;
  }
  window.dispatchEvent(
    new CustomEvent<HubLiveConnectionDetail>(HUB_LIVE_CONNECTION_EVENT, { detail }),
  );
}

export function onHubLiveConnectionChange(
  listener: (detail: HubLiveConnectionDetail) => void,
): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }
  const handler = (event: Event): void => {
    const custom = event as CustomEvent<HubLiveConnectionDetail>;
    if (custom.detail == null) {
      return;
    }
    listener(custom.detail);
  };
  window.addEventListener(HUB_LIVE_CONNECTION_EVENT, handler);
  return () => {
    window.removeEventListener(HUB_LIVE_CONNECTION_EVENT, handler);
  };
}

export function hubLiveConnectionBannerMessage(
  detail: HubLiveConnectionDetail,
): string | null {
  if (detail.status === "disconnected") {
    return detail.reason === "auth"
      ? HUB_LIVE_CONNECTION_AUTH_MESSAGE
      : HUB_LIVE_CONNECTION_DISCONNECTED_MESSAGE;
  }
  if (detail.status === "reconnecting") {
    return HUB_LIVE_CONNECTION_RECONNECTING_MESSAGE;
  }
  return null;
}
